/**
 * High-frequency simulation clock used by the playback RAF loop and
 * imperative vehicle transforms. Redux `simulationTime` is only synced ~5Hz
 * for UI (scrubber, panels) so React does not re-render every frame.
 *
 * React subscribers (highlights / targets) are notified at most ~20Hz;
 * imperative paint uses simulationFrameBus every tick.
 */
import { simulationFrameBus } from './simulationFrameBus';

type Listener = (time: number) => void;

/** Min gap between React clock notifications while playing. */
const REACT_NOTIFY_INTERVAL_MS = 50;

class SimulationClockStore {
  private time = 0;
  private listeners = new Set<Listener>();
  private lastReactNotifyMs = 0;

  getTime(): number {
    return this.time;
  }

  /**
   * @param time simulation seconds
   * @param wallDtS wall-clock delta for this tick (0 = seek / Redux sync)
   */
  setTime(time: number, wallDtS = 0) {
    const changed = this.time !== time;
    if (!changed && wallDtS <= 0) return;

    if (changed) {
      this.time = time;
    }

    // Imperative consumers every tick (same call stack as the clock rAF).
    simulationFrameBus.emit(this.time, wallDtS);

    // Throttle React subscribers while playing; always notify on seek/sync.
    const now = performance.now();
    const shouldNotifyReact =
      wallDtS <= 0 ||
      this.lastReactNotifyMs === 0 ||
      now - this.lastReactNotifyMs >= REACT_NOTIFY_INTERVAL_MS;
    if (shouldNotifyReact && this.listeners.size > 0) {
      this.lastReactNotifyMs = now;
      this.listeners.forEach((listener) => listener(this.time));
    }
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

export const simulationClockStore = new SimulationClockStore();
